import React, {
  forwardRef, useContext, useEffect, useMemo, useState, useCallback,
} from 'react';
import { result } from 'underscore';
import { ListBarTreeRegular, ChevronRight20Regular } from '@fluentui/react-icons';
import {
  MenuButton,
  Tooltip,
  makeStyles,
  mergeClasses,
  Popover,
  shorthands,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { UserContext } from '@bingads-webui-react/user-context';
import { AccountSelectorPopOver } from '@bingads-webui-react/global-menu';
import { TreeViewWithRCIDStorage } from '@bingads-webui-react/hierarcy-tree-view-with-rcid-storage';
import { CustomerAccountSelectorTreeView } from '@bingads-webui-react/customer-account-selector';
import { LayoutSize } from '@bingads-webui-fluent-component/layout-size';
import { MenuContext } from '@bingads-webui-fluent-component/global-menu-container';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ClientCenterCustomerService } from '@bingads-webui/clientcenter-customer-service';

import type { II18n, AccountItemProps } from './types';

const useStyles = makeStyles({
  name: {
    fontWeight: '700',
    ...shorthands.flex(1, 1, '0px'),
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
  },
  number: {
    fontWeight: '400',
  },
  button: {
    height: '32px',
    minWidth: '48px',
    maxWidth: '240px',
  },
  fullButton: {
    backgroundColor: 'white',
  },
  popover: {
    maxWidth: 'initial',
    ...shorthands.padding('0px'),
  },
  selector: {
  },
  itemButton: {
    fontWeight: '400',
    backgroundColor: 'transparent',
    paddingLeft: '2px',
    justifyContent: 'space-around',
  },
});
const AccountSelector = React.memo(TreeViewWithRCIDStorage(CustomerAccountSelectorTreeView));

const getPerMarker = (perfMarker) => {
  if (perfMarker == null) {
    return null;
  }
  const marker = perfMarker.createChild('accounts-item');
  marker.willInit();
  return marker;
};

export const AccountsItem = forwardRef<HTMLButtonElement, AccountItemProps>(({
  className, as, account, perfMarker,
}, ref) => {
  const [componentPerfMarker] = useState(() => getPerMarker(perfMarker));
  const i18n: II18n = useContext(I18nContext);
  const { scope }: any = useContext(ScopeContext);
  const userContext: any = useContext(UserContext);
  const { layoutSize } = useContext(MenuContext);
  const { CurrentCustomer: customer } = userContext;
  const [toolTipVisible, setToolTipVisible] = useState(false);
  const [showAccountSelectorView, setShowAccountSelectorView] = useState(false);
  const [open, setOpen] = useState(false);

  const classes = useStyles();
  const selectedEntity = useMemo(() => (result(scope.values, 'aid') > 0 ? {
    id: account.Id,
    name: account.Name,
    number: account.AccountNumber,
    isCustomer: false,
  } : {
    id: customer.Id,
    name: customer.Name,
    number: customer.Number,
    isCustomer: true,
  }), [account.AccountNumber,
    account.Id,
    account.Name,
    customer.Id,
    customer.Name,
    customer.Number,
    scope.values,
  ]);
  const showAccountName = layoutSize === LayoutSize.XXL;
  const ccCustomerService = (param) => new ClientCenterCustomerService(param);
  const switchAccountsLabelClicked = useCallback(() => setShowAccountSelectorView(true), []);

  const onOpenChange = (e, data) => {
    if (!e.currentTarget) {
      if (!showAccountSelectorView) {
        setOpen(data.open);
      }
      return;
    }

    const targetId = e.currentTarget.id;

    const isOpen = data.open
      || (
        (targetId === 'trigger-as-menu-button' || targetId === 'trigger-as-menu-item')
        && (e.type === 'click')
        && !showAccountSelectorView);
    setOpen(isOpen);
  };

  useEffect(() => {
    if (!open) {
      setShowAccountSelectorView(false);
    }
  }, [open]);

  const onClick = useCallback(() => {
    setShowAccountSelectorView(true);
  }, []);

  useEffect(() => {
    if (componentPerfMarker) {
      componentPerfMarker.done();
    }
  }, [componentPerfMarker]);

  return (
    <Popover open={open} openOnHover trapFocus onOpenChange={onOpenChange}>
      <PopoverTrigger disableButtonEnhancement>
        {(as === MenuButton) ? (
          <Tooltip
            content={`${selectedEntity.name} (${selectedEntity.number})`}
            relationship="label"
            visible={toolTipVisible && !showAccountName && !open}
            onVisibleChange={(_ev, data) => setToolTipVisible(data.visible)}
          >
            <MenuButton
              className={mergeClasses('accounts-item', classes.button, showAccountName ? classes.fullButton : '', className, 'nav-item', 'accounts-item')}
              icon={showAccountName ? undefined : <ListBarTreeRegular />}
              appearance="subtle"
              onClick={onClick}
              ref={ref}
              id="trigger-as-menu-button"
            >
              {showAccountName && (
              <>
                <span className={classes.name}>
                  {selectedEntity.name}
                </span>
                <span>&nbsp;</span>
                <span className={classes.number}>
                  {`(${selectedEntity.number})`}
                </span>
              </>
              )}
            </MenuButton>
          </Tooltip>
        ) : (
          <MenuButton
            className={mergeClasses('accounts-item', 'nav-menu-item', classes.itemButton, className)}
            icon={<ListBarTreeRegular />}
            onClick={onClick}
            appearance="subtle"
            menuIcon={<ChevronRight20Regular />}
            ref={ref}
            id="trigger-as-menu-item"
          >
            {i18n.getString(_TL_('Account'))}
          </MenuButton>
        )}
      </PopoverTrigger>
      <PopoverSurface className={mergeClasses('account-selector-popup', classes.popover)} aria-label={i18n.getString(_TL_('account selector'))}>
        <AccountSelectorPopOver
          classNames={classes.selector}
          AccountSelector={AccountSelector}
          ccCustomerService={ccCustomerService}
          selectedEntity={selectedEntity}
          showAccountSelectorView={showAccountSelectorView}
          switchAccountsLabelClicked={switchAccountsLabelClicked}
          perfMarker={perfMarker}
        />
      </PopoverSurface>
    </Popover>
  );
});
